import React, { useRef, useEffect, useState } from 'react';
import { motion, useScroll, useTransform, useAnimation } from 'framer-motion';
import { ArrowRight, ChevronRight, Target, Crosshair } from 'lucide-react';
import { Link } from 'react-router-dom';
import { VideoBackground } from './VideoBackground';
import { GlitchText, TacticalTextReveal } from './TacticalAnimations';
import { TechLabel } from '../common/TacticalCard';

const ImmersiveHero = ({ 
  title = 'TACTICAL',
  subtitle = 'OUTDOOR WEAR',
  description = 'Zorlu koşullar için tasarlandı.',
  videoUrl = '',
  imageUrl = '',
  ctaText = 'MAĞAZAYI KEŞFET',
  ctaLink = 'https://www.taktikstore.com',
  bannerLink = ''
}) => {
  const ref = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const controls = useAnimation();

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start']
  });
  
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);

  // Mouse parallax effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      setMousePosition({
        x: (clientX / innerWidth - 0.5) * 20,
        y: (clientY / innerHeight - 0.5) * 20
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Initial animation
  useEffect(() => {
    controls.start({
      opacity: 1,
      transition: { duration: 1.5 }
    });
  }, [controls]);

  return (
    <section 
      ref={ref}
      className="relative min-h-screen flex items-center justify-center overflow-hidden" 
      data-testid="immersive-hero"
    >
      {/* Video/Image Background with Parallax */}
      <motion.div 
        className="absolute inset-0"
        style={{ y, scale }}
      >
        <VideoBackground 
          videoUrl={videoUrl}
          fallbackImage={imageUrl || 'https://images.unsplash.com/photo-1569242838421-c6f57b9a02df?w=1920&q=80'}
          showControls={!!videoUrl}
        />
      </motion.div>

      {/* Grid Overlay */}
      <div className="absolute inset-0 animated-grid pointer-events-none" />

      {/* Scanlines */}
      <div className="absolute inset-0 scanline-overlay opacity-20" />

      {/* Noise Texture */}
      <div className="noise-texture" />

      {/* Tactical HUD Elements */}
      <TacticalHUD mousePosition={mousePosition} />

      {/* Content */}
      <motion.div 
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32"
        style={{ opacity }}
        initial={{ opacity: 0 }}
        animate={controls}
      >
        <motion.div 
          className="max-w-3xl"
          style={{
            x: mousePosition.x * 0.5,
            y: mousePosition.y * 0.5
          }}
        >
          {/* Status Labels */}
          <motion.div 
            className="flex flex-wrap gap-2 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <TechLabel variant="accent">
              <span className="status-indicator mr-2" />
              [OPERATIONAL]
            </TechLabel>
            <TechLabel variant="default">[EST. 2020]</TechLabel>
          </motion.div>

          {/* Main Heading with Glitch Effect */}
          <motion.h1 
            className="font-display text-6xl sm:text-7xl lg:text-8xl text-white mb-6 leading-none"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            <span className="text-gradient-gold block mb-2">
              {title}
            </span>
            <motion.span
              className="block"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8 }}
            >
              {subtitle}
            </motion.span>
          </motion.h1>

          {/* Description */}
          <motion.p 
            className="text-vav-fg-muted text-lg sm:text-xl mb-8 max-w-xl leading-relaxed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
          >
            {description}
          </motion.p>

          {/* CTA Buttons */}
          <motion.div 
            className="flex flex-wrap gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2 }}
          >
            <a 
              href={ctaLink}
              target="_blank"
              rel="noopener noreferrer"
              className="tactical-btn btn-shine flex items-center gap-2 group" 
              data-testid="hero-shop-btn"
            >
              <Target className="w-4 h-4 group-hover:rotate-90 transition-transform duration-300" />
              {ctaText}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <Link 
              to="/about" 
              className="tactical-btn-outline flex items-center gap-2 group"
            >
              MARKAMIZ
              <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div 
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <div className="w-6 h-10 border-2 border-vav-gold/30 rounded-full flex items-start justify-center p-2">
            <motion.div 
              className="w-1 h-2 bg-vav-gold rounded-full"
              animate={{ y: [0, 8, 0] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
            />
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

// Tactical HUD Elements
const TacticalHUD = ({ mousePosition }) => {
  return (
    <>
      {/* Top Left - Coordinates */}
      <motion.div 
        className="absolute top-8 left-8 hidden lg:block z-20"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.5 }}
        style={{
          x: mousePosition.x * -0.3,
          y: mousePosition.y * -0.3
        }}
      >
        <div className="font-mono text-xs text-vav-gold/50 space-y-1 border-l-2 border-vav-gold/30 pl-3">
          <p className="flex items-center gap-2">
            <Crosshair className="w-3 h-3" />
            LAT: 41.0082°N
          </p>
          <p>LON: 28.9784°E</p>
          <p className="text-vav-accent-green">
            <span className="inline-block w-2 h-2 rounded-full bg-vav-accent-green mr-2 animate-pulse" />
            ACTIVE
          </p>
        </div>
      </motion.div>

      {/* Top Right - Mission Stats */}
      <motion.div 
        className="absolute top-8 right-8 hidden lg:block z-20"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.6 }}
        style={{
          x: mousePosition.x * 0.3,
          y: mousePosition.y * -0.3
        }}
      >
        <div className="font-mono text-xs text-vav-gold/50 space-y-1 text-right border-r-2 border-vav-gold/30 pr-3">
          <p>TEMP: -10°C / +45°C</p>
          <p>WATER: RESISTANT</p>
          <p>DURABILITY: MAXIMUM</p>
        </div>
      </motion.div>

      {/* Bottom Left - Status */}
      <motion.div 
        className="absolute bottom-8 left-8 hidden lg:block z-20"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.7 }}
      >
        <div className="font-mono text-xs text-vav-gold/50">
          <p className="mb-1">SYSTEM STATUS</p>
          <div className="flex items-center gap-2">
            <div className="w-16 h-1 bg-vav-bg-subtle rounded-full overflow-hidden">
              <motion.div 
                className="h-full bg-vav-accent-green"
                initial={{ width: 0 }}
                animate={{ width: '100%' }}
                transition={{ delay: 2, duration: 1 }}
              />
            </div>
            <span className="text-vav-accent-green">READY</span>
          </div>
        </div>
      </motion.div>

      {/* Radar Effect - Bottom Right */}
      <motion.div 
        className="absolute bottom-20 right-20 w-32 h-32 hidden lg:block z-10"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 0.3, scale: 1 }}
        transition={{ delay: 1.8 }}
      >
        {/* Radar circles */}
        <motion.div
          className="absolute inset-0 border border-vav-gold/30 rounded-full"
          animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
          transition={{ repeat: Infinity, duration: 3 }}
        />
        <motion.div
          className="absolute inset-4 border border-vav-gold/20 rounded-full"
          animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0, 0.3] }}
          transition={{ repeat: Infinity, duration: 3, delay: 0.5 }}
        />
        {/* Sweep line */}
        <motion.div
          className="absolute inset-0 overflow-hidden rounded-full"
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 4, ease: "linear" }}
        >
          <div className="absolute top-1/2 left-1/2 w-1/2 h-px bg-gradient-to-r from-vav-gold/60 to-transparent origin-left" />
        </motion.div>
        {/* Center dot */}
        <div className="absolute top-1/2 left-1/2 w-2 h-2 -translate-x-1/2 -translate-y-1/2 bg-vav-gold rounded-full" />
        {/* Inner ring */}
        <div className="absolute inset-8 border border-vav-gold/10 rounded-full" />
      </motion.div>

      {/* Corner Brackets */}
      <div className="absolute top-4 left-4 w-8 h-8 border-t-2 border-l-2 border-vav-gold/20 hidden lg:block" />
      <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-vav-gold/20 hidden lg:block" />
      <div className="absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 border-vav-gold/20 hidden lg:block" />
      <div className="absolute bottom-4 right-4 w-8 h-8 border-b-2 border-r-2 border-vav-gold/20 hidden lg:block" />
    </>
  );
};

export default ImmersiveHero;
